import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { notifications } from '@mantine/notifications';
import styles from './AppBar.module.scss';
import {
  FiCountry,
  FiLanguage,
  apiLanguageToFiLanguage,
  fiLanguageToI18nCode,
  useLocale,
} from '../../contexts/LocaleContext';
import { getFiGrowthBook } from '../../lib/growthbook';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import * as types from '../../types';
import { validateCart } from '../../lib/cart';
import { logInternalError } from '../../lib/errors';
import { getProductsBySku } from '../../lib/product';
import { storeShopActions } from '../../reducers/storeShop';
import Twemoji from 'react-twemoji';

const DEFAULT_SUPPORTED_LANGUAGES: string[] = ['EN_US', 'EN_CA', 'FR_CA', 'EN_GB'];

function useOnClickOutside(ref: React.RefObject<HTMLElement>, handler: () => void) {
  useEffect(() => {
    const listener = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (!ref.current || !target || ref.current.contains(target)) return;
      handler();
    };
    document.addEventListener('pointerdown', listener);
    return () => {
      document.removeEventListener('pointerdown', listener);
    };
  }, [ref, handler]);
}

function useCloseOnScroll(open: boolean, handler: () => void) {
  useEffect(() => {
    if (!open) return;
    const onScroll = () => handler();
    window.addEventListener('scroll', onScroll, {
      capture: true,
      passive: true,
    } as EventListenerOptions);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [open, handler]);
}

const COUNTRY_NAME_KEYS: Record<FiCountry, string> = {
  [FiCountry.US]: 'united_states',
  [FiCountry.CA]: 'canada',
  [FiCountry.GB]: 'united_kingdom',
  [FiCountry.FR]: 'france',
  [FiCountry.DE]: 'germany',
  [FiCountry.ES]: 'spain',
  [FiCountry.FI]: 'finland',
  [FiCountry.MT]: 'malta',
  [FiCountry.RO]: 'romania',
  [FiCountry.AT]: 'austria',
  [FiCountry.SI]: 'slovenia',
  [FiCountry.LV]: 'latvia',
  [FiCountry.SK]: 'slovakia',
  [FiCountry.BE]: 'belgium',
  [FiCountry.LU]: 'luxembourg',
  [FiCountry.LI]: 'liechtenstein',
  [FiCountry.NL]: 'netherlands',
  [FiCountry.SE]: 'sweden',
  [FiCountry.DK]: 'denmark',
  [FiCountry.NO]: 'norway',
  [FiCountry.CH]: 'switzerland',
  [FiCountry.IS]: 'iceland',
  [FiCountry.TR]: 'turkey',
};

export default function CountrySelector() {
  const { country, setCountry, language, setLanguage, flagEmoji } = useLocale();
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();

  const cart = useSelector((state: types.AppState) => state.storeShop.cart);
  const products = useSelector((state: types.AppState) => state.config.products);
  const internalTester = useSelector((state: types.AppState) => !!state.config.siteConfig.internalTester);

  const fiSupportedCountries = useMemo(
    () =>
      getFiGrowthBook().getFeatureValue<FiCountry[]>('ecom-supported-countries', [FiCountry.US, FiCountry.CA], {
        internalTester,
      }),
    [internalTester],
  );

  const showSelector = fiSupportedCountries.length > 1;

  const supportedLanguages = useMemo(() => {
    const raw = getFiGrowthBook().getFeatureValue<string[]>('ecom-supported-languages', DEFAULT_SUPPORTED_LANGUAGES, {
      internalTester,
    });
    return raw.map(apiLanguageToFiLanguage).filter((l): l is FiLanguage => l != null);
  }, [internalTester]);

  useOnClickOutside(menuRef, () => setOpen(false));
  useCloseOnScroll(open, () => setOpen(false));

  const { t } = useTranslation();

  const options = useMemo(() => {
    // Canada options (pinned at top, alphabetically first)
    const canadaOptions: { key: string; label: string; emoji: string }[] = [];
    if (supportedLanguages.includes(FiLanguage.EN_CA)) {
      canadaOptions.push({
        key: 'en-CA',
        label: t('canada_english'),
        emoji: '🇨🇦',
      });
    }
    if (supportedLanguages.includes(FiLanguage.FR_CA)) {
      canadaOptions.push({
        key: 'fr-CA',
        label: t('canada_french'),
        emoji: '🇨🇦',
      });
    }

    // Remaining countries in alphabetical order, filtered by feature flag
    const otherOptions = [
      { key: 'AT', label: t('austria'), emoji: '🇦🇹' },
      { key: 'BE', label: t('belgium'), emoji: '🇧🇪' },
      { key: 'CH', label: t('switzerland'), emoji: '🇨🇭' },
      { key: 'DK', label: t('denmark'), emoji: '🇩🇰' },
      { key: 'FI', label: t('finland'), emoji: '🇫🇮' },
      { key: 'FR', label: t('france'), emoji: '🇫🇷' },
      { key: 'DE', label: t('germany'), emoji: '🇩🇪' },
      { key: 'IS', label: t('iceland'), emoji: '🇮🇸' },
      { key: 'LV', label: t('latvia'), emoji: '🇱🇻' },
      { key: 'LI', label: t('liechtenstein'), emoji: '🇱🇮' },
      { key: 'LU', label: t('luxembourg'), emoji: '🇱🇺' },
      { key: 'MT', label: t('malta'), emoji: '🇲🇹' },
      { key: 'NL', label: t('netherlands'), emoji: '🇳🇱' },
      { key: 'NO', label: t('norway'), emoji: '🇳🇴' },
      { key: 'RO', label: t('romania'), emoji: '🇷🇴' },
      { key: 'SE', label: t('sweden'), emoji: '🇸🇪' },
      { key: 'SK', label: t('slovakia'), emoji: '🇸🇰' },
      { key: 'SI', label: t('slovenia'), emoji: '🇸🇮' },
      { key: 'ES', label: t('spain'), emoji: '🇪🇸' },
      { key: 'TR', label: t('turkey'), emoji: '🇹🇷' },
      { key: 'GB', label: t('united_kingdom'), emoji: '🇬🇧' },
      { key: 'US', label: t('united_states_of_america'), emoji: '🇺🇸' },
    ].filter((opt) => opt.key === 'US' || fiSupportedCountries.includes(opt.key as FiCountry));

    return [...canadaOptions, ...otherOptions];
  }, [supportedLanguages, fiSupportedCountries, t]);
  const handleSelect = useCallback(
    async (key: string) => {
      let targetCountry: FiCountry;
      let targetLanguage: FiLanguage;

      if (key === 'US') {
        targetCountry = FiCountry.US;
        targetLanguage = FiLanguage.EN_US;
      } else if (key === 'en-CA') {
        targetCountry = FiCountry.CA;
        targetLanguage = FiLanguage.EN_CA;
      } else if (key === 'fr-CA') {
        targetCountry = FiCountry.CA;
        targetLanguage = FiLanguage.FR_CA;
      } else if (key === 'GB') {
        targetCountry = FiCountry.GB;
        targetLanguage = FiLanguage.EN_GB;
      } else if (key === 'FR') {
        targetCountry = FiCountry.FR;
        targetLanguage = FiLanguage.FR_FR;
      } else if (key === 'DE') {
        targetCountry = FiCountry.DE;
        targetLanguage = FiLanguage.DE_DE;
      } else if (key === 'ES') {
        targetCountry = FiCountry.ES;
        targetLanguage = FiLanguage.ES_ES;
      } else if (key === 'FI') {
        targetCountry = FiCountry.FI;
        targetLanguage = FiLanguage.EN_FI;
      } else if (key === 'MT') {
        targetCountry = FiCountry.MT;
        targetLanguage = FiLanguage.EN_MT;
      } else if (key === 'RO') {
        targetCountry = FiCountry.RO;
        targetLanguage = FiLanguage.RO_RO;
      } else if (key === 'AT') {
        targetCountry = FiCountry.AT;
        targetLanguage = FiLanguage.DE_AT;
      } else if (key === 'SI') {
        targetCountry = FiCountry.SI;
        targetLanguage = FiLanguage.EN_SI;
      } else if (key === 'BE') {
        targetCountry = FiCountry.BE;
        targetLanguage = FiLanguage.NL_BE;
      } else if (key === 'CH') {
        targetCountry = FiCountry.CH;
        targetLanguage = FiLanguage.DE_CH;
      } else if (key === 'DK') {
        targetCountry = FiCountry.DK;
        targetLanguage = FiLanguage.EN_DK;
      } else if (key === 'IS') {
        targetCountry = FiCountry.IS;
        targetLanguage = FiLanguage.EN_IS;
      } else if (key === 'LI') {
        targetCountry = FiCountry.LI;
        targetLanguage = FiLanguage.DE_LI;
      } else if (key === 'LU') {
        targetCountry = FiCountry.LU;
        targetLanguage = FiLanguage.FR_LU;
      } else if (key === 'LV') {
        targetCountry = FiCountry.LV;
        targetLanguage = FiLanguage.EN_LV;
      } else if (key === 'NL') {
        targetCountry = FiCountry.NL;
        targetLanguage = FiLanguage.NL_NL;
      } else if (key === 'NO') {
        targetCountry = FiCountry.NO;
        targetLanguage = FiLanguage.EN_NO;
      } else if (key === 'SE') {
        targetCountry = FiCountry.SE;
        targetLanguage = FiLanguage.EN_SE;
      } else if (key === 'SK') {
        targetCountry = FiCountry.SK;
        targetLanguage = FiLanguage.EN_SK;
      } else if (key === 'TR') {
        targetCountry = FiCountry.TR;
        targetLanguage = FiLanguage.EN_TR;
      } else {
        return;
      }

      setOpen(false);
      setCountry(targetCountry);
      setLanguage(targetLanguage);

      if (Object.keys(cart.cartItems).length > 0) {
        // t has not been updated with the new language yet, so we need to specify it for the notification
        const lng = fiLanguageToI18nCode(targetLanguage);
        try {
          const invalidIds = await validateCart(cart, targetCountry);
          if (invalidIds.length > 0) {
            dispatch(storeShopActions.removeCartItems({ ids: invalidIds }));

            const productMap = getProductsBySku(products);
            const invalidProductNames = invalidIds
              .map((id) => {
                const cartItem = cart.cartItems[id];
                if (!cartItem) return null;
                return productMap.get(cartItem.lineItem.sku)?.name ?? cartItem.lineItem.sku;
              })
              .filter(Boolean)
              .join(', ');

            notifications.show({
              message: t('cart_items_not_available_in_country', {
                lng,
                productNames: invalidProductNames || t('cart', { lng }),
                country: t(COUNTRY_NAME_KEYS[targetCountry], { lng }),
                count: invalidIds.length,
              }),
              color: 'red',
            });
          }
        } catch (err) {
          logInternalError(err, { tags: { source: 'CountrySelector.validateCart' } });
          dispatch(storeShopActions.removeAllCartItems());
        }
      }
    },
    [setCountry, setLanguage, cart, products, dispatch, t],
  );

  if (!showSelector) return null;

  return (
    <div className={styles.countrySelector} ref={menuRef}>
      <button
        type="button"
        className={styles.countryButton}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <Twemoji options={{ className: styles.flagIcon }}>{flagEmoji}</Twemoji>
      </button>
      {open && (
        <div className={classNames(styles.countryMenu)} role="menu">
          {options.map((opt) => {
            let isSelected: boolean;
            switch (opt.key) {
              case 'en-CA':
                isSelected = country === FiCountry.CA && language === FiLanguage.EN_CA;
                break;
              case 'fr-CA':
                isSelected = country === FiCountry.CA && language === FiLanguage.FR_CA;
                break;
              default:
                isSelected = opt.key === country;
                break;
            }
            return (
              <button
                key={opt.key}
                type="button"
                className={classNames(styles.countryMenuItem, {
                  [styles.selected]: isSelected,
                })}
                onClick={() => void handleSelect(opt.key)}
                role="menuitem"
              >
                <Twemoji options={{ className: styles.flagIcon }}>{opt.emoji}</Twemoji>
                <span className={styles.countryMenuLabel}>{opt.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
